import React from "react";
import { groupBy } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { buildLink } from "common/routing";
import { toSentenceCase } from "common/utils/strings";
import {
  GetMySubmissionsQuery,
  useGetMySubmissionsQuery,
} from "../../../generated/graphql";
import { spacing, styled } from "../../../stitches.config";
import { FlexColumn } from "../../Common/__styles__/Layout";
import { EmptyState } from "../../Common/EmptyState";
import { Body, Title } from "../../Common/Typography";
import { Card } from "../__styles__/Card";
import AccessDeniedPage, { ACCESS_DENIED_RESOURCE } from "../AccessDeniedPage";
import Layout from "../Layout";

const CategoryContainer = styled(FlexColumn, {
  marginTop: spacing.l,

  "&:not(:first-of-type)": {
    marginTop: spacing.xl,
  },
});

const MyRecords = () => {
  const { data, loading } = useGetMySubmissionsQuery();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  if (!loading && !data?.currentPublicUser) {
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.PAGE} />;
  }

  const renderContent = () => {
    if (loading) {
      return (
        <Body size="default" type="regular">
          Loading...
        </Body>
      );
    }

    const currentUser = data!.currentPublicUser!;

    if (currentUser.submissions.length === 0) {
      return (
        <EmptyState
          title={t("my-submissions-empty-state-header")}
          body={t("my-submissions-empty-state-text")}
          icon="no-activity-beach"
        />
      );
    }

    const submissionsByCategory = groupBy(
      currentUser.submissions,
      s => s.submissionTypeVersion.submissionType.category,
    );

    const submissionToNode = (
      submission: NonNullable<
        GetMySubmissionsQuery["currentPublicUser"]
      >["submissions"][number],
    ) => {
      const propertyFullAddress = submission.property?.fullAddress;
      const submissionTypeName =
        submission.submissionTypeVersion.submissionType.name;

      return (
        <FlexColumn gap="xs">
          <Body size="default" type="emphasis">
            {submissionTypeName}
            {propertyFullAddress && ` - ${propertyFullAddress}`}
          </Body>
          <Body size="small" type="regular" color="contentSecondary">
            {`${currentUser.firstName} ${currentUser.lastName} · ${currentUser.email}`}
          </Body>
        </FlexColumn>
      );
    };

    return (
      <>
        {Object.entries(submissionsByCategory).map(
          ([category, submissions]) => (
            <CategoryContainer key={category}>
              <Body
                size="default"
                type="emphasis"
                style={{ marginBottom: spacing.m.value }}
              >
                {toSentenceCase(category)}
              </Body>
              {submissions.map(submission => (
                <Card
                  key={submission.id}
                  onClick={() =>
                    history.push({
                      pathname: buildLink("viewSubmission", {
                        submissionId: submission.id,
                      }),
                      state: {
                        prevLocation: location.pathname + location.search,
                      },
                    })
                  }
                >
                  {submissionToNode(submission)}
                </Card>
              ))}
            </CategoryContainer>
          ),
        )}
      </>
    );
  };

  return (
    <Layout hasGutters>
      <div style={{ padding: `${spacing.l.value} 0` }}>
        <Title
          size="default"
          type="semiBold"
          style={{ marginBottom: spacing.l.value }}
        >
          {t("my-submissions-header")}
        </Title>
        {renderContent()}
      </div>
    </Layout>
  );
};

export default MyRecords;
